import { useState, useEffect, useRef } from 'react';
import { Download, X } from 'lucide-react';
import { ToolHeader } from '@/components/ui/ToolHeader';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Field } from '@/components/ui/Input';
import { Slider } from '@/components/ui/Slider';
import { Select } from '@/components/ui/Select';
import { Dropzone } from '@/components/ui/Dropzone';
import { Badge } from '@/components/ui/Badge';
import { useApp } from '@/store/app';
import { formatBytes, loadImage, readFileAsDataURL } from '@/lib/utils';
import { processAndSave, canvasToBlob, stripExt } from '@/lib/bulk';

export function ImageCompress() {
  const { pendingFile, setPendingFile } = useApp();
  const [files, setFiles] = useState<File[]>([]);
  const [previewImg, setPreviewImg] = useState<HTMLImageElement | null>(null);
  const [quality, setQuality] = useState(75);
  const [format, setFormat] = useState<'jpeg' | 'webp'>('jpeg');
  const [maxW, setMaxW] = useState(0);
  const [outBlob, setOutBlob] = useState<Blob | null>(null);
  const [outUrl, setOutUrl] = useState('');
  const [working, setWorking] = useState(false);
  const [progress, setProgress] = useState({ done: 0, total: 0 });
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    if (pendingFile && pendingFile.type.startsWith('image/')) {
      handleFiles([pendingFile]);
      setPendingFile(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function handleFiles(incoming: File[]) {
    if (!incoming.length) return;
    const next = [...files, ...incoming.filter((f) => f.type.startsWith('image/'))];
    setFiles(next);
    if (!previewImg && next[0]) {
      const url = await readFileAsDataURL(next[0]);
      const im = await loadImage(url);
      setPreviewImg(im);
      setMaxW(im.width);
    }
  }

  function removeFile(i: number) {
    const next = files.filter((_, idx) => idx !== i);
    setFiles(next);
    if (next.length === 0) {
      setPreviewImg(null);
      setMaxW(0);
    } else if (i === 0) {
      readFileAsDataURL(next[0]).then(loadImage).then((im) => {
        setPreviewImg(im);
        setMaxW(im.width);
      });
    }
  }

  // Live preview reflects FIRST image with current settings.
  useEffect(() => {
    if (!previewImg || !canvasRef.current) return;
    const c = canvasRef.current;
    const targetW = maxW || previewImg.width;
    const ratio = targetW / previewImg.width;
    c.width = targetW;
    c.height = Math.round(previewImg.height * ratio);
    const ctx = c.getContext('2d');
    if (!ctx) return;
    ctx.imageSmoothingQuality = 'high';
    ctx.drawImage(previewImg, 0, 0, c.width, c.height);
    c.toBlob(
      (blob) => {
        if (!blob) return;
        setOutBlob(blob);
        const url = URL.createObjectURL(blob);
        setOutUrl((prev) => {
          if (prev) URL.revokeObjectURL(prev);
          return url;
        });
      },
      `image/${format}`,
      quality / 100,
    );
  }, [previewImg, quality, format, maxW]);

  async function exportAll() {
    if (files.length === 0) return;
    setWorking(true);
    setProgress({ done: 0, total: files.length });
    try {
      const ext = format === 'jpeg' ? 'jpg' : format;
      await processAndSave(
        files,
        async (file) => {
          const url = await readFileAsDataURL(file);
          const im = await loadImage(url);
          // Apply max-width per file (cap, don't upscale)
          const targetW = maxW > 0 ? Math.min(maxW, im.width) : im.width;
          const ratio = targetW / im.width;
          const c = document.createElement('canvas');
          c.width = targetW;
          c.height = Math.round(im.height * ratio);
          const ctx = c.getContext('2d')!;
          ctx.imageSmoothingQuality = 'high';
          ctx.drawImage(im, 0, 0, c.width, c.height);
          const blob = await canvasToBlob(c, `image/${format}`, quality / 100);
          return { blob, filename: `${stripExt(file.name)}-min.${ext}` };
        },
        {
          zipName: 'compressed',
          onProgress: (done, total) => setProgress({ done, total }),
        },
      );
    } finally {
      setWorking(false);
      setProgress({ done: 0, total: 0 });
    }
  }

  return (
    <div>
      <ToolHeader
        toolId="image-compress"
        title="Image compress"
        description="Reduce image file size with quality control. Drop multiple files to bulk-export."
        badges={
          <>
            {files.length > 1 && <Badge variant="accent">{files.length} files</Badge>}
            {files[0] && outBlob && (
              <Badge variant={outBlob.size < files[0].size ? 'success' : 'warning'}>
                {outBlob.size < files[0].size
                  ? `−${Math.round(((files[0].size - outBlob.size) / files[0].size) * 100)}%`
                  : 'larger'}
              </Badge>
            )}
          </>
        }
      />

      {files.length === 0 ? (
        <Dropzone accept="image/*" multiple onFiles={handleFiles} hint="PNG, JPG, WebP — drop one or many" />
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <Card className="lg:order-2 space-y-4">
            <Field label="Quality" hint={`${quality}%`}>
              <Slider value={quality} onChange={setQuality} min={10} max={100} showValue={false} />
            </Field>
            <Field label="Format">
              <Select
                value={format}
                onChange={(e) => setFormat(e.target.value as typeof format)}
                options={[
                  { value: 'jpeg', label: 'JPEG' },
                  { value: 'webp', label: 'WebP' },
                ]}
              />
            </Field>
            <Field label="Max width" hint={`${maxW || previewImg?.width || 0}px`}>
              <Slider
                value={maxW || previewImg?.width || 0}
                onChange={setMaxW}
                min={100}
                max={Math.max(100, previewImg?.width || 4000)}
                step={20}
                showValue={false}
              />
            </Field>

            <div className="border-t border-border pt-3 space-y-2">
              <SizeRow label="First — original" size={files[0]?.size ?? 0} />
              <SizeRow
                label="First — compressed"
                size={outBlob?.size ?? 0}
                delta={files[0] && outBlob ? outBlob.size - files[0].size : undefined}
              />
              {files.length > 1 && (
                <div className="text-2xs text-fg-subtle pt-1 border-t border-border">
                  Same settings apply to all {files.length} files on export.
                </div>
              )}
            </div>

            <div className="grid grid-cols-2 gap-2">
              <Button
                variant="secondary"
                size="sm"
                onClick={() => {
                  setFiles([]);
                  setPreviewImg(null);
                }}
              >
                Clear
              </Button>
              <Button variant="primary" size="sm" loading={working} onClick={exportAll}>
                <Download size={11} />
                {files.length > 1 ? `Export ${files.length}` : 'Save'}
              </Button>
            </div>
            {working && progress.total > 1 && (
              <div className="text-2xs text-fg-subtle">
                Processing {progress.done} / {progress.total}…
              </div>
            )}
          </Card>

          <Card className="lg:col-span-2 lg:order-1 !p-0 overflow-hidden">
            <div className="px-4 py-2.5 border-b border-border text-xs font-medium text-fg-muted">
              Preview {files.length > 1 && <span className="text-fg-subtle">· first of {files.length}</span>}
            </div>
            <div className="p-6 bg-bg-inset min-h-[360px] flex items-center justify-center">
              {outUrl && (
                <img src={outUrl} alt="preview" className="max-w-full max-h-[420px] rounded-md shadow-card" />
              )}
              <canvas ref={canvasRef} className="hidden" />
            </div>
            {files.length > 1 && (
              <div className="border-t border-border px-3 py-2">
                <div className="flex flex-wrap gap-1.5">
                  {files.map((f, i) => (
                    <div
                      key={`${f.name}-${i}`}
                      className="flex items-center gap-1 pl-2 pr-1 py-0.5 text-2xs rounded-md bg-bg-inset border border-border max-w-[200px]"
                    >
                      <span className="truncate">{f.name}</span>
                      <button
                        onClick={() => removeFile(i)}
                        className="shrink-0 size-4 rounded hover:bg-surface-hover text-fg-subtle hover:text-danger flex items-center justify-center"
                      >
                        <X size={9} />
                      </button>
                    </div>
                  ))}
                  <label className="flex items-center gap-1 px-2 py-0.5 text-2xs rounded-md border border-dashed border-border hover:border-accent hover:bg-accent/5 cursor-pointer">
                    + add more
                    <input
                      type="file"
                      multiple
                      accept="image/*"
                      onChange={(e) => {
                        const list = e.target.files;
                        if (!list || !list.length) return;
                        handleFiles(Array.from(list));
                        e.target.value = '';
                      }}
                      className="hidden"
                    />
                  </label>
                </div>
              </div>
            )}
          </Card>
        </div>
      )}
    </div>
  );
}

function SizeRow({ label, size, delta }: { label: string; size: number; delta?: number }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-xs text-fg-muted">{label}</span>
      <div className="flex items-center gap-2">
        <span className="text-sm font-mono tabular-nums">{formatBytes(size)}</span>
        {typeof delta === 'number' && delta !== 0 && (
          <Badge variant={delta < 0 ? 'success' : 'warning'}>
            {delta < 0 ? '−' : '+'}
            {formatBytes(Math.abs(delta))}
          </Badge>
        )}
      </div>
    </div>
  );
}

export default ImageCompress;
